/**
 * Pre-connect metadata dialog for OAuth-style linked accounts.
 *
 * The provider popup still verifies the account; this dialog captures the
 * user-facing label and agent guidance before the OAuth flow starts.
 */

import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type {
  ConnectionProfileInput,
  MeConnection,
} from "@/services/me-connections-service";

interface ConnectAccountProfileDialogProps {
  conn: MeConnection;
  onClose: () => void;
  onContinue: (profile: ConnectionProfileInput) => void;
}

export function ConnectAccountProfileDialog({
  conn,
  onClose,
  onContinue,
}: ConnectAccountProfileDialogProps) {
  const [accountLabel, setAccountLabel] = useState(conn.displayName);
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(conn.linkedAccounts.length === 0);
  const [error, setError] = useState<string | null>(null);

  const continueToProvider = () => {
    if (!accountLabel.trim()) {
      setError("Account name is required.");
      return;
    }
    onContinue({
      accountLabel: accountLabel.trim(),
      description: description.trim(),
      isDefault,
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Describe this {conn.displayName} account</DialogTitle>
          <DialogDescription>
            Add context so agents know when to use this account after you connect it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="connect-account-label">Account name</Label>
            <Input
              id="connect-account-label"
              value={accountLabel}
              onChange={(event) => setAccountLabel(event.target.value)}
              placeholder="Work Gmail"
              autoComplete="off"
            />
            <p className="text-xs leading-5 text-muted-foreground">
              Agents use this name to distinguish accounts, such as work, personal,
              or shared inbox.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="connect-account-description">Description for agents</Label>
            <Textarea
              id="connect-account-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Use this for customer and team communication."
              rows={3}
            />
            <p className="text-xs leading-5 text-muted-foreground">
              Tell agents when this account should be used. Mention teams,
              workflows, or data boundaries.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <Checkbox
              checked={isDefault}
              onCheckedChange={(checked) => setIsDefault(checked === true)}
            />
            Use by default for {conn.displayName}
          </label>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={continueToProvider}>Continue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
